import CloseIcon from '@mui/icons-material/Close';
import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { useScreenWidth } from '@/hooks/useScreenWidth';
import { useToast } from '@/components/Toast';
import useRenterInviteDrawerStore from '@/stores/useRenterInviteDrawerStore';
import RenterInviteDrawerFormContent from './RenterInviteDrawerFormContent';
import { theme } from '@theguarantors/tg-package-material-ui';
import { SplitFeatureFlag } from '@/utils/split/splitConfig';
import { useSplitFeatureFlag } from '@/hooks';
import { Renter } from '@/utils/types/renter.interface';
import { createSuccessToastParams } from './RenterInvite.SuccessToast';

const getDrawerProps = (isDesktop: boolean) =>
  isDesktop
    ? {
        headerPadding: { py: 2, px: 4 },
        contentPadding: { py: 3, pl: 4, pr: '10px' },
        paperWidth: '520px',
        anchor: 'right' as const,
        paperHeight: '100%',
        borderRadius: '0',
      }
    : {
        headerPadding: { pt: '24px', pb: '16px', px: 2 },
        contentPadding: { py: '16px', px: 2 },
        paperWidth: '100%',
        anchor: 'bottom' as const,
        paperHeight: 'min-content',
        borderRadius: '24px 24px 0 0',
      };

const RenterInviteDrawer = () => {
  const { isDesktop } = useScreenWidth();
  const { isOpen, isFormDirty, closeDrawer, openCleanRenterInviteDrawerForm, formKey } =
    useRenterInviteDrawerStore();
  const { isFeatureFlagEnabled } = useSplitFeatureFlag(
    SplitFeatureFlag.MONARCH_460_NEW_RENTER_INVITE_IN_DRAWER,
  );

  const queryClient = useQueryClient();
  const toast = useToast();
  const drawerProps = getDrawerProps(isDesktop);

  const showSuccessToast = (emails: string[]) => {
    if (!isFeatureFlagEnabled) {
      toast.success('Invitation sent successfully');
      return;
    }

    toast.success(
      createSuccessToastParams({
        emails,
        onInviteAnother: openCleanRenterInviteDrawerForm,
        theme,
      }),
    );
  };

  const handleSuccess = (values: Renter) => {
    const emails = values.applicantEmails?.map((e) => e.value).filter(Boolean) ?? [];
    closeDrawer();
    showSuccessToast(emails);
    queryClient.invalidateQueries({ queryKey: ['applications'] });
    queryClient.invalidateQueries({ queryKey: ['applications-pagination'] });
  };

  const backdrop = {
    id: 'renter-invite-drawer-backdrop',
    'data-testid': 'renter-invite-drawer-backdrop',
    style: {
      backgroundColor: isDesktop ? 'rgba(8, 10, 45, 0.1)' : 'rgba(8, 10, 45, 0.3)',
    },
  };

  return (
    <Drawer
      anchor={drawerProps.anchor}
      open={isOpen}
      onClose={() => {
        if (!isFormDirty()) {
          closeDrawer();
        }
      }}
      slotProps={{ backdrop }}
      sx={{
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          border: 'none',
          borderRadius: drawerProps.borderRadius,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          height: drawerProps.paperHeight,
          width: drawerProps.paperWidth,
          overflow: 'hidden',
          [theme.breakpoints.down('md')]: {
            maxHeight: 'calc(100vh - 105px)',
          },
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          borderBottom: `1px solid ${theme.palette.neutralGrey?.[200] ?? theme.palette.grey[200]}`,
          ...drawerProps.headerPadding,
        }}
      >
        <Box
          sx={{
            width: '100%',
            backgroundColor: 'background.default',
            flexShrink: 0,
          }}
          p={0}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{
                fontSize: '24px !important',
                lineHeight: '36px',
                [theme.breakpoints.down('md')]: {
                  fontSize: '18px !important',
                  lineHeight: '24px !important',
                },
              }}
            >
              Invite your applicants
            </Typography>
            <IconButton
              color="inherit"
              aria-label="close drawer"
              onClick={closeDrawer}
              sx={{ mr: '-12px' }}
              edge="start"
            >
              <CloseIcon
                sx={{
                  [theme.breakpoints.down('md')]: {
                    fontSize: '20px',
                  },
                }}
              />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Stack
        sx={{
          flex: 1,
          overflowY: 'auto',
          boxSizing: 'border-box',
          ...drawerProps.contentPadding,
          pb: 0,
          [theme.breakpoints.down('md')]: {
            overflowX: 'hidden',
          },
        }}
      >
        <RenterInviteDrawerFormContent key={formKey} onSuccess={handleSuccess} />
      </Stack>
    </Drawer>
  );
};

export default RenterInviteDrawer;
